import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EduShell, EmptyState, StatusBadge, TEACHER_NAV } from "@/components/EduShell";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { downloadCsv, eduRpc, formatDue } from "@/lib/education";
import { AssignmentAiSection } from "@/components/AiActivity";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/teacher/assignments/$assignmentId")({
  component: TeacherAssignmentDetail,
  head: () => ({
    meta: [
      { title: "Assignment - Inquira teacher" },
      { name: "description", content: "Review student submissions, grade work and leave feedback on an Inquira assignment." },
      { property: "og:title", content: "Review and grade an assignment" },
      { property: "og:description", content: "Submissions, scores and feedback for one assignment." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface Assignment {
  id: string;
  class_id: string;
  title: string;
  instructions: string | null;
  kind: string | null;
  status: string | null;
  due_at: string | null;
  points: number | null;
  ai_mode: string | null;
}

interface Submission {
  submission_id: string;
  student_id: string;
  student_name: string;
  status: string;
  score: number | null;
  feedback: string | null;
  note: string | null;
  submitted_at: string | null;
  answers: unknown;
}

function TeacherAssignmentDetail() {
  const { assignmentId } = useParams({ from: "/teacher/assignments/$assignmentId" });
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [subs, setSubs] = useState<Submission[]>([]);
  const [draft, setDraft] = useState<Record<string, { score: string; feedback: string }>>({});
  const [newDue, setNewDue] = useState("");

  const load = useCallback(async () => {
    const [{ data: a }, s] = await Promise.all([
      supabase
        .from("assignments")
        .select("id, class_id, title, instructions, kind, status, due_at, points, ai_mode")
        .eq("id", assignmentId)
        .maybeSingle(),
      eduRpc<Submission[]>("edu_assignment_submissions", { _assignment_id: assignmentId }),
    ]);
    setAssignment((a as Assignment) ?? null);
    setSubs(s.data ?? []);
  }, [assignmentId]);

  useEffect(() => { void load(); }, [load]);

  async function publish() {
    if (!assignment) return;
    const { error } = await eduRpc("edu_save_assignment", {
      _assignment_id: assignment.id,
      _class_id: assignment.class_id,
      _title: assignment.title,
      _status: "published",
    });
    if (error) { toast.error(error); return; }
    toast.success(i18n.t("edu.teacher_assignments_assignmentId.published_to_the_class"));
    void load();
  }

  async function grade(s: Submission, action: "save" | "return") {
    const d = draft[s.submission_id] ?? { score: String(s.score ?? ""), feedback: s.feedback ?? "" };
    const { error } = await eduRpc("edu_grade_submission", {
      _submission_id: s.submission_id,
      _score: d.score === "" ? null : Number(d.score),
      _feedback: d.feedback || null,
      // The RPC accepts 'grade' | 'return' | 'request_resubmit'. Saving without
      // releasing to the student is the 'grade' action.
      _action: action === "return" ? "return" : "grade",
    });
    if (error) { toast.error(error); return; }
    toast.success(action === "return" ? "Returned to the student" : "Grade saved");
    void load();
  }

  async function extend() {
    if (!newDue) return;
    const { error } = await eduRpc("edu_extend_deadline", {
      _assignment_id: assignmentId,
      _due_at: new Date(newDue).toISOString(),
    });
    if (error) { toast.error(error); return; }
    setNewDue("");
    toast.success(i18n.t("edu.teacher_assignments_assignmentId.deadline_updated"));
    void load();
  }

  async function remove() {
    const { error } = await eduRpc("edu_delete_assignment", { _assignment_id: assignmentId });
    if (error) { toast.error(error); return; }
    toast.success(i18n.t("edu.teacher_assignments_assignmentId.assignment_deleted"));
    void navigate({ to: "/teacher/assignments" });
  }

  return (
    <EduShell
      nav={TEACHER_NAV}
      title={assignment?.title ?? "Assignment"}
      subtitle={assignment ? `${assignment.kind ?? "homework"} · ${formatDue(assignment.due_at)} · ${assignment.points ?? 0} points` : undefined}
      actions={
        <>
          {assignment?.status !== "published" && <Button onClick={publish}><T k="edu.teacher_assignments_assignmentId.publish" /></Button>}
          <Button
            variant="outline"
            onClick={() =>
              downloadCsv(
                `submissions-${assignmentId}.csv`,
                subs.map((s) => ({
                  student: s.student_name,
                  status: s.status,
                  score: s.score ?? "",
                  submitted_at: s.submitted_at ?? "",
                  feedback: s.feedback ?? "",
                })),
              )
            }
          >
            Export CSV
          </Button>
          <Button variant="outline" onClick={remove}><T k="edu.teacher_assignments_assignmentId.delete" /></Button>
        </>
      }
    >
      <AssignmentAiSection assignmentId={assignmentId} />

      {assignment?.instructions && (
        <div className="mb-6 whitespace-pre-wrap rounded-2xl border border-rule bg-card p-4 text-sm text-ink">
          {assignment.instructions}
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-end gap-2 rounded-2xl border border-rule bg-card p-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted"><T k="edu.teacher_assignments_assignmentId.extend_deadline" /></p>
          <input
            type="datetime-local"
            className="mt-1 rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            value={newDue}
            onChange={(e) => setNewDue(e.target.value)}
          />
        </div>
        <Button variant="outline" onClick={extend}><T k="edu.teacher_assignments_assignmentId.update" /></Button>
      </div>

      <h2 className="mb-3 font-display text-lg font-bold text-ink"><T k="edu.teacher_assignments_assignmentId.submissions" /></h2>
      {subs.length === 0 ? (
        <EmptyState title={i18n.t("edu.teacher_assignments_assignmentId.no_submissions_yet")} body="Students appear here once the assignment is published." />
      ) : (
        <ul className="space-y-3">
          {subs.map((s) => {
            const d = draft[s.submission_id] ?? { score: s.score === null ? "" : String(s.score), feedback: s.feedback ?? "" };
            return (
              <li key={s.submission_id} className="rounded-2xl border border-rule bg-card p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-ink">{s.student_name}</span>
                  <span className="flex items-center gap-2 text-xs text-ink-muted">
                    <StatusBadge status={s.status} />
                    {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : "Not submitted"}
                  </span>
                </div>
                {s.note && <p className="mt-2 whitespace-pre-wrap text-sm text-ink">{s.note}</p>}
                {Array.isArray(s.answers) && (s.answers as unknown[]).length > 0 && (
                  <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-ink">
                    {(s.answers as Array<{ answer?: string }>).map((a, i) => (
                      <li key={i}>{a?.answer || "—"}</li>
                    ))}
                  </ol>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    type="number"
                    placeholder={i18n.t("edu.teacher_assignments_assignmentId.score")}
                    className="w-24 rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
                    value={d.score}
                    onChange={(e) => setDraft({ ...draft, [s.submission_id]: { ...d, score: e.target.value } })}
                  />
                  <input
                    placeholder={i18n.t("edu.teacher_assignments_assignmentId.feedback_for_the_student")}
                    className="min-w-[200px] flex-1 rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
                    value={d.feedback}
                    onChange={(e) => setDraft({ ...draft, [s.submission_id]: { ...d, feedback: e.target.value } })}
                  />
                  <Button variant="outline" onClick={() => grade(s, "save")}><T k="edu.teacher_assignments_assignmentId.save" /></Button>
                  <Button onClick={() => grade(s, "return")}><T k="edu.teacher_assignments_assignmentId.return" /></Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </EduShell>
  );
}
